import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'

import { ChartDetails } from '../types'
import { spark2ApiUrl } from '@/config/consts'
import { Timeframe } from '@/ui/charts/defaults'
import { useFilterChartDataByTimeframe } from '@/ui/charts/logic/useFilterDataByTimeframe'

const HISTORY_CUTOFF = new Date('2025-06-17T08:00:00Z')

export const SpkStakingHistoryTimeframes = ['7D', '1M', '1Y', 'All'] as const satisfies Timeframe[]
export type SpkStakingHistoryTimeframes = (typeof SpkStakingHistoryTimeframes)[number]

export function useChartDetails(): ChartDetails {
  const [timeframe, setTimeframe] = useState<SpkStakingHistoryTimeframes>('All')
  const filterDataByTimeframe = useFilterChartDataByTimeframe(timeframe)

  const result = useQuery({
    queryKey: ['spk-staking', 'chart-details'],
    queryFn: async () => {
      const res = await fetch(`${spark2ApiUrl}/spk-staking/stats/historic/`)
      if (!res.ok) {
        throw new Error(`Failed to fetch spk staking historic data: ${res.statusText}`)
      }

      const data = historicDataResponseSchema.parse(await res.json())

      return data.filter((result) => result.date >= HISTORY_CUTOFF)
    },
    select: filterDataByTimeframe,
  })

  return {
    history: result,
    onTimeframeChange: (timeframe: Timeframe) => {
      if (SpkStakingHistoryTimeframes.includes(timeframe)) {
        setTimeframe(timeframe as any)
      }
    },
    timeframe,
    availableTimeframes: SpkStakingHistoryTimeframes,
  }
}

const historicDataResponseSchema = z
  .array(
    z.object({
      datetime: z.string().transform((value) => new Date(`${value}Z`)),
      total_staked: z.string().transform((value) => NormalizedNumber(value)),
    }),
  )
  .transform((results) =>
    results.map((result) => ({
      date: result.datetime,
      totalStaked: result.total_staked,
    })),
  )
